import { useState, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { Delete, Check } from 'lucide-react';

interface NumpadProps {
  onConfirm: (hours: number) => void;
  onCancel: () => void;
  maxHours?: number;
  label?: string;
}

/**
 * Numpad tactil per a entrada d'hores
 * Dissenyat per a us amb guants en entorn de taller
 * Minim 44x44px per boto (WCAG touch target)
 */
export function Numpad({ onConfirm, onCancel, maxHours = 12, label = 'Hores a imputar' }: NumpadProps) {
  const [value, setValue] = useState<string>('0');
  const [hasDecimal, setHasDecimal] = useState(false);

  const handleDigit = useCallback((digit: string) => {
    setValue((prev) => {
      if (prev === '0' && digit !== '.') {
        return digit;
      }
      // Limit to 2 decimal places
      if (hasDecimal) {
        const parts = prev.split('.');
        if (parts[1] && parts[1].length >= 2) {
          return prev;
        }
      }
      // Limit total value
      const newValue = prev + digit;
      if (parseFloat(newValue) > maxHours) {
        return prev;
      }
      return newValue;
    });
  }, [hasDecimal, maxHours]);

  const handleDecimal = useCallback(() => {
    if (!hasDecimal) {
      setValue((prev) => prev + '.');
      setHasDecimal(true);
    }
  }, [hasDecimal]);

  const handleBackspace = useCallback(() => {
    setValue((prev) => {
      if (prev.length === 1) {
        setHasDecimal(false);
        return '0';
      }
      const newValue = prev.slice(0, -1);
      if (!newValue.includes('.')) {
        setHasDecimal(false);
      }
      return newValue;
    });
  }, []);

  const handleClear = useCallback(() => {
    setValue('0');
    setHasDecimal(false);
  }, []);

  const handleConfirm = useCallback(() => {
    const hours = parseFloat(value);
    if (hours > 0 && hours <= maxHours) {
      onConfirm(hours);
    }
  }, [value, maxHours, onConfirm]);

  const hours = parseFloat(value) || 0;
  const isValid = hours > 0 && hours <= maxHours;

  // Quick time buttons (in hours)
  const quickTimes = [
    { label: '15m', value: 0.25 },
    { label: '30m', value: 0.5 },
    { label: '1h', value: 1 },
    { label: '2h', value: 2 },
    { label: '4h', value: 4 },
    { label: '8h', value: 8 },
  ];

  const handleQuickTime = (time: number) => {
    setValue(time.toString());
    setHasDecimal(time % 1 !== 0);
  };

  return (
    <div className="bg-slate-900 rounded-2xl p-4 w-full max-w-sm mx-auto">
      {/* Label */}
      <p className="text-slate-400 text-center text-sm mb-2">{label}</p>

      {/* Display */}
      <div className="bg-slate-800 rounded-xl p-4 mb-4">
        <div className="text-5xl font-bold text-center text-white tabular-nums">
          {value}
          <span className="text-2xl text-slate-400 ml-1">h</span>
        </div>
        {hours > 0 && (
          <div className="text-center text-slate-500 text-sm mt-1">
            = {Math.floor(hours)}h {Math.round((hours % 1) * 60)}min
          </div>
        )}
      </div>

      {/* Quick Time Buttons */}
      <div className="grid grid-cols-6 gap-2 mb-4">
        {quickTimes.map((qt) => (
          <button
            key={qt.label}
            onClick={() => handleQuickTime(qt.value)}
            className={cn(
              'h-11 rounded-lg font-medium text-sm transition-colors',
              'bg-slate-700 text-slate-200 hover:bg-slate-600 active:bg-slate-500'
            )}
          >
            {qt.label}
          </button>
        ))}
      </div>

      {/* Numpad Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <button
            key={digit}
            onClick={() => handleDigit(digit)}
            className={cn(
              'h-14 rounded-xl text-2xl font-semibold transition-colors',
              'bg-slate-700 text-white hover:bg-slate-600 active:bg-slate-500',
              'min-w-[44px] min-h-[44px]'
            )}
          >
            {digit}
          </button>
        ))}

        {/* Bottom row: decimal, 0, backspace */}
        <button
          onClick={handleDecimal}
          disabled={hasDecimal}
          className={cn(
            'h-14 rounded-xl text-2xl font-semibold transition-colors',
            'bg-slate-700 text-white hover:bg-slate-600 active:bg-slate-500',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          .
        </button>
        <button
          onClick={() => handleDigit('0')}
          className={cn(
            'h-14 rounded-xl text-2xl font-semibold transition-colors',
            'bg-slate-700 text-white hover:bg-slate-600 active:bg-slate-500'
          )}
        >
          0
        </button>
        <button
          onClick={handleBackspace}
          className={cn(
            'h-14 rounded-xl text-sm font-semibold transition-colors',
            'bg-slate-600 text-white hover:bg-slate-500 active:bg-slate-400',
            'flex items-center justify-center'
          )}
        >
          <Delete className="h-6 w-6" />
        </button>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={onCancel}
          className={cn(
            'h-14 rounded-xl text-sm font-semibold transition-colors',
            'bg-slate-700 text-slate-300 hover:bg-slate-600 active:bg-slate-500'
          )}
        >
          Cancel·lar
        </button>
        <button
          onClick={handleConfirm}
          disabled={!isValid}
          className={cn(
            'h-14 rounded-xl text-sm font-semibold transition-colors',
            'flex items-center justify-center gap-2',
            isValid
              ? 'bg-emerald-600 text-white hover:bg-emerald-500 active:bg-emerald-400'
              : 'bg-slate-700 text-slate-500 cursor-not-allowed'
          )}
        >
          <Check className="h-5 w-5" />
          Confirmar
        </button>
      </div>

      {/* Clear button */}
      <button
        onClick={handleClear}
        className="w-full mt-3 h-10 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-300 transition-colors"
      >
        Esborrar tot
      </button>
    </div>
  );
}
